import {
	OrderQuoteStatus,
	useGetClientOrderQuoteQuery,
	useGetClientOrdersQuery,
	useRecordQuoteView,
	useRemoveOrderQuoteItemMutation,
	useRequestOrderQuoteOfferMutation,
} from '@codezo/graphql';
import { buildSpecificationRows } from '@codezo/product-specs';
import {
	Card,
	MicroLabel,
	QuoteAddress,
	QuoteFacts,
	QuoteHeader,
	QuoteOverview,
	QuoteSections,
	QuoteSectionsSkeleton,
	RequestedProducts,
	SectionHead,
	type LifecycleStage,
	type QuoteLayoutSection,
	type QuoteStat,
	type RequestedProduct,
} from '@codezo/quote-detail';
import { Check, ChevronRight, Clock, MoreHorizontal, Pencil, Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { AddProductsToQuoteModal } from '../components/AddProductsToQuoteModal';
import { EditOrderQuoteAddressModal } from '../components/EditOrderQuoteAddressModal';
import { EditOrderQuoteDetailsModal } from '../components/EditOrderQuoteDetailsModal';
import {
	EditOrderQuoteItemModal,
	type EditOrderQuoteItemValue,
} from '../components/EditOrderQuoteItemModal';
import { QuoteOffersTab, type ClientItemAttachment } from '../components/QuoteOffersTab';
import { ItemAttachments } from './ItemAttachments';
import { useQuoteClientView } from '../context';
import { ORDER_STATUS, statusConfig } from '../../../../lib/status-configs';
import { formatDate } from '../../../../lib/utils';
import { Button } from '../../../../components/ui/button';
import { ConfirmDialog } from '../../../../components/ui/confirm-dialog';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '../../../../components/ui/dropdown-menu';
import { FilePreview, type FilePreviewItem } from '../../../../components/ui/file-preview';
import { ProductImage, orderedProductImages } from '../../../../components/ui/product-image';
import { StatusBadge } from '../../../../components/ui/status-badge';
import { ClientOfferBatchV2 } from './ClientOfferBatchV2';

/* The sections that share a tab. Declared once so every member names the same
   group — a typo here would silently split a tab in two. */
const PRODUCTS_TAB = { id: 'products', label: 'Products & offer' };
const DETAILS_TAB = { id: 'details', label: 'Details' };

const STATUS_CONFIG: Record<
	string,
	{ label: string; tone: 'success' | 'warn' | 'info' | 'neutral' | 'danger' }
> = {
	[OrderQuoteStatus.Draft]: { label: 'Draft', tone: 'neutral' },
	[OrderQuoteStatus.Requested]: { label: 'Requested', tone: 'info' },
	[OrderQuoteStatus.Pending]: { label: 'Pending', tone: 'warn' },
	[OrderQuoteStatus.PendingReview]: { label: 'Awaiting confirmation', tone: 'info' },
	[OrderQuoteStatus.Submitted]: { label: 'Submitted', tone: 'info' },
	[OrderQuoteStatus.Accepted]: { label: 'Accepted', tone: 'success' },
	[OrderQuoteStatus.Rejected]: { label: 'Rejected', tone: 'danger' },
	[OrderQuoteStatus.Expired]: { label: 'Expired', tone: 'neutral' },
};

interface QuoteDetailPageV2Props {
	quoteId: string;
}

/**
 * The client's quote page, v2.
 *
 * The same page frame the admin reads — `QuoteHeader`, `QuoteOverview` and
 * `QuoteSections` out of `@codezo/quote-detail` — with the sections a client has
 * any business seeing. That is the point of the rewrite: the two sides of a
 * quote were two independently drawn pages that happened to describe the same
 * record, and every change had to be made twice or the record was described two
 * ways.
 *
 * What is deliberately NOT here, and is on the admin's page: managers, vetters,
 * quote views, payment settings, activity, and every margin figure. Those are
 * how we run the quote, not what was quoted — the modules take them as optional
 * props precisely so this page can decline them rather than blank them out.
 *
 * The page owns the queries, the mutations, the modals and the formatting; the
 * modules own the layout. `v1` remains at the caller's choosing — see
 * `QuoteClientView`'s `design` prop.
 */
export function QuoteDetailPageV2({ quoteId }: QuoteDetailPageV2Props) {
	const { onOpenOrder, readOnlyReason } = useQuoteClientView();
	/* An admin previewing the guest view. The affordances stay on screen —
	   removing them would make the preview lie about what a guest sees — but
	   nothing behind them can run, so they are disabled and say why. */
	const readOnly = !!readOnlyReason;

	const [viewer, setViewer] = useState<{ files: FilePreviewItem[]; index: number } | null>(null);
	const [addItemsOpen, setAddItemsOpen] = useState(false);
	const [editItem, setEditItem] = useState<EditOrderQuoteItemValue | null>(null);
	const [removeItemId, setRemoveItemId] = useState<string | null>(null);
	const [editDetailsOpen, setEditDetailsOpen] = useState(false);
	const [editAddressOpen, setEditAddressOpen] = useState(false);
	/* Files by quote line, lifted out of the offers tab — see its
	   `onAttachmentsChange` for why they can only arrive that way. */
	const [itemAttachments, setItemAttachments] = useState<Map<string, ClientItemAttachment[]>>(
		new Map()
	);
	/* Sidebar or tabs, remembered per browser. Its own key rather than the
	   admin's: the admin previews this very page inside their own app, on the same
	   origin, and one shared key would have their preview silently re-arrange
	   their quote page too. */

	const {
		data,
		loading,
		networkStatus,
		refetch: refetchQuote,
	} = useGetClientOrderQuoteQuery({
		variables: { id: quoteId },
		skip: !quoteId,
		fetchPolicy: 'no-cache',
		notifyOnNetworkStatusChange: true,
	});

	const [removeItem, { loading: removingItem }] = useRemoveOrderQuoteItemMutation();
	const [requestOffer, { loading: requestingOffer }] = useRequestOrderQuoteOfferMutation();

	// Counts as a client-side view in both the client portal and the guest shell —
	// the backend reads the channel off the session, so this one call covers both.
	useRecordQuoteView(quoteId);

	const quote = data?.getClientOrderQuote;
	const isInitialLoading = loading && networkStatus === 1;

	/* The orders this quote turned into. Asked for HERE rather than inside the
	   section, because whether there are any decides whether the section exists at
	   all — and a tab that opens on nothing is worse than no tab.
	   The quote carries no reference to them; the link only points the other way
	   (`Order.orderQuoteId`). */
	const tooEarlyForOrders =
		!quote ||
		quote.status === OrderQuoteStatus.Draft ||
		quote.status === OrderQuoteStatus.Requested;
	const { data: ordersData } = useGetClientOrdersQuery({
		variables: { orderQuoteId: quoteId },
		skip: tooEarlyForOrders,
		fetchPolicy: 'no-cache',
	});
	const orders = ordersData?.getClientOrders ?? [];

	const items = useMemo(() => quote?.items ?? [], [quote]);

	const handleRemoveItem = async () => {
		if (!removeItemId) return;
		try {
			await removeItem({ variables: { id: removeItemId } });
			toast.success('Item removed');
			setRemoveItemId(null);
			refetchQuote();
		} catch {
			toast.error('Failed to remove item');
		}
	};

	const handleRequestOffer = async () => {
		try {
			const res = await requestOffer({ variables: { orderQuoteId: quoteId } });
			const result = res.data?.requestOrderQuoteOffer;
			if (result && result.success === false) {
				toast.error(result.message || 'Could not request an offer');
				return;
			}
			toast.success('Offer requested — the team will prepare your offer');
			refetchQuote();
		} catch {
			toast.error('Failed to request an offer');
		}
	};

	if (isInitialLoading || !quote) {
		return <DetailSkeleton />;
	}

	const status = STATUS_CONFIG[quote.status] ?? { label: quote.status, tone: 'neutral' as const };
	const isEditable =
		quote.status === OrderQuoteStatus.Draft || quote.status === OrderQuoteStatus.Requested;
	const canRequestOffer = quote.status === OrderQuoteStatus.Draft;
	const hasItems = items.length > 0;

	const stats: QuoteStat[] = [
		{
			label: 'Products',
			value: String(items.length),
			hint: hasItems ? 'on this quote' : 'nothing added yet',
		},
		{
			label: 'Offers',
			value: String(quote.offerBatchCount ?? 0),
			hint:
				quote.offerBatchCount > 0
					? 'ready for you to compare'
					: quote.status === OrderQuoteStatus.Draft
						? 'request one when you’re ready'
						: 'we’re preparing yours',
		},
		{
			label: 'Your deadline',
			value: quote.deadline ? formatDate(quote.deadline) : '—',
			hint: quote.deadline ? 'when you need this by' : 'no date given',
		},
	];

	const stages: LifecycleStage[] = (quote.lifecycle ?? []).map((step) => ({
		key: step.step,
		label: step.label,
		state: step.completed ? 'done' : step.current ? 'current' : 'upcoming',
	}));

	const requested: RequestedProduct[] = items.map((item) => {
		/* The snapshot is the only source for a quote — it is what we quoted
		   against, and with the catalogue join gone there is nothing to fall back
		   to. A line added before snapshots existed simply has no specifications;
		   unlike the admin's copy, we don't tell the client the record is missing,
		   because there is nothing they can do about it. */
		const rows = buildSpecificationRows({
			specifications: item.productSpecifications,
			dimensions: item.productDimensions,
			weight: item.productWeight,
		});
		const ordered = orderedProductImages(item.productImages).filter(
			(i): i is typeof i & { url: string } => !!i.url
		);

		return {
			id: item.id,
			product: {
				id: item.productId,
				name: item.productName || 'Unknown product',
				sku: item.productSku,
			},
			/* Primary first, then the rest in their own order — the card reads this
			   as a hero and a strip of thumbnails under it. */
			media: ordered.map((img, i) => (
				<ProductImage
					key={`${item.id}-${i}`}
					image={img}
					alt={item.productName ?? undefined}
					className="h-full w-full object-cover"
				/>
			)),
			onOpenImages: ordered.length
				? (index: number) =>
						setViewer({
							files: ordered.map((img, i) => ({
								id: `${item.id}-${i}`,
								url: img.url,
								name: item.productName ?? 'Product image',
								mimeType: 'image/jpeg',
							})),
							index,
						})
				: undefined,
			quantities: item.quantities.map((q) => q.quantity.toLocaleString()),
			deadline: deadlineLabel(item.deadline),
			description: item.productDescription,
			specs: rows.map((row) => [row.label, row.value] as const),
			note: item.productSpecifications
				? `Specifications as recorded on this quote ${formatDate(
						item.productSpecificationsAt!
					)}`
				: null,
			/* The product's files, under the product — where they are looked for.
			   Lists nothing until an offer carrying this line has been sent, which is
			   the only point a client can read them at all. */
			files: <ItemAttachments files={itemAttachments.get(item.id) ?? []} />,
			actions: isEditable ? (
				<DropdownMenu>
					<DropdownMenuTrigger className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-text-3 transition-colors hover:bg-surface-hover hover:text-text-1">
						<MoreHorizontal size={14} />
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end">
						<DropdownMenuItem
							disabled={readOnly}
							onClick={() =>
								setEditItem({
									id: item.id,
									productName: item.productName || 'Unknown',
									quantities: item.quantities.map((q) => ({
										id: q.id,
										quantity: q.quantity,
									})),
									deadline: item.deadline ?? null,
								})
							}
						>
							<Pencil size={14} /> Edit item
						</DropdownMenuItem>
						<DropdownMenuItem
							variant="destructive"
							disabled={readOnly}
							onClick={() => setRemoveItemId(item.id)}
						>
							<Trash2 size={14} /> Remove
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			) : undefined,
		};
	});

	/* Every section, declared once. `QuoteSections` decides where each one lands —
	   down one column beside the index rail, or behind its tab. What was asked for
	   and what was offered share the first tab: you compare an offer against the
	   request, so reading one without the other is the wrong unit of work. */
	const sections: QuoteLayoutSection[] = [
		{
			id: 'requested',
			label: 'Your products',
			count: items.length,
			tabGroup: PRODUCTS_TAB,
			content: (
				<>
					<SectionHead
						title="Your products"
						meta="what you asked us to price"
						action={
							isEditable ? (
								<Button
									variant="outline"
									size="sm"
									onClick={() => setAddItemsOpen(true)}
									disabled={readOnly}
									title={readOnlyReason}
								>
									<Plus size={12} data-icon="inline-start" />
									Add products
								</Button>
							) : undefined
						}
					/>
					<RequestedProducts
						items={requested}
						emptyState={
							<div className="px-4 py-11 text-center text-sm text-text-4">
								{isEditable
									? 'Add products to this quote to get started.'
									: 'No products were added to this quote.'}
							</div>
						}
					/>
				</>
			),
		},
		{
			id: 'offers',
			label: 'Offers',
			count: quote.offerBatchCount ?? 0,
			tabGroup: PRODUCTS_TAB,
			content: (
				<>
					<SectionHead
						title="Offers"
						meta="pick a quantity, a delivery and a sample for each product — the total follows"
					/>
					<QuoteOffersTab
						quoteId={quoteId}
						quoteName={quote.name}
						quoteItems={items}
						quoteDeadline={quote.deadline}
						quoteDeliveryAddress={quote.deliveryAddress ?? null}
						organizationId={quote.organizationId}
						onQuoteChanged={refetchQuote}
						skip={false}
						onAttachmentsChange={setItemAttachments}
						renderBatch={(props) => <ClientOfferBatchV2 {...props} />}
					/>
				</>
			),
		},
		/* Only when there is something to open. A tab that leads to "no orders yet"
		   is a promise the page can't keep — before acceptance, the answer to
		   "where's my order" is the offer section, not an empty list. */
		...(orders.length > 0
			? [
					{
						id: 'orders',
						label: orders.length > 1 ? 'Orders' : 'Order',
						count: orders.length,
						content: (
							<>
								<SectionHead
									title={orders.length > 1 ? 'Your orders' : 'Your order'}
									meta="what this quote turned into"
								/>
								<Card>
									{orders.map((order, i) => {
										const config = statusConfig(ORDER_STATUS, order.status);
										return (
											<button
												key={order.id}
												type="button"
												onClick={() => onOpenOrder?.(order.id)}
												disabled={!onOpenOrder}
												className="flex w-full items-center gap-3 bg-transparent px-4 py-3 text-left transition-colors enabled:hover:bg-surface-hover disabled:cursor-default"
												style={{
													borderTop:
														i > 0
															? '0.5px solid var(--line-1)'
															: 'none',
												}}
											>
												<span className="min-w-0 flex-1">
													<span className="block truncate text-base font-medium text-text-1">
														#{order.orderNumber}
													</span>
													{order.createdAt && (
														<span className="mt-px block text-sm text-text-4">
															Created {formatDate(order.createdAt)}
														</span>
													)}
												</span>
												<StatusBadge
													active
													activeLabel={config.label}
													activeTone={config.tone}
												/>
												{onOpenOrder && (
													<ChevronRight
														size={15}
														className="shrink-0 text-text-4"
													/>
												)}
											</button>
										);
									})}
								</Card>
							</>
						),
					} satisfies QuoteLayoutSection,
				]
			: []),
		{
			id: 'details',
			label: 'Details',
			tabGroup: DETAILS_TAB,
			content: (
				<>
					<SectionHead
						title="Details"
						meta="what this quote is for and where it ships"
						action={
							isEditable ? (
								<Button
									variant="ghost"
									size="sm"
									onClick={() => setEditDetailsOpen(true)}
									disabled={readOnly}
									title={readOnlyReason}
								>
									<Pencil size={12} data-icon="inline-start" />
									Edit
								</Button>
							) : undefined
						}
					/>
					<div className="grid gap-3 md:grid-cols-2">
						<QuoteFacts
							title="Quote details"
							facts={[
								{
									label: 'Purpose',
									value: quote.purpose
										? quote.purposeOther
											? `${quote.purpose.replace(/_/g, ' ').toLowerCase()} — ${quote.purposeOther}`
											: quote.purpose.replace(/_/g, ' ').toLowerCase()
										: 'Not specified',
									muted: !quote.purpose,
								},
								{
									label: 'Deadline',
									value: quote.deadline ? formatDate(quote.deadline) : '—',
									muted: !quote.deadline,
								},
								{
									label: 'Description',
									value: quote.description || 'No description',
									muted: !quote.description,
								},
							]}
						/>
						<QuoteAddress
							action={
								isEditable ? (
									<Button
										variant="ghost"
										size="sm"
										onClick={() => setEditAddressOpen(true)}
										disabled={readOnly}
										title={readOnlyReason}
									>
										{quote.deliveryAddress ? (
											<Pencil size={11} data-icon="inline-start" />
										) : (
											<Plus size={11} data-icon="inline-start" />
										)}
										{quote.deliveryAddress ? 'Edit' : 'Set address'}
									</Button>
								) : undefined
							}
							lines={
								quote.deliveryAddress
									? [
											quote.deliveryAddress.addressLine1,
											quote.deliveryAddress.addressLine2,
											[
												quote.deliveryAddress.postalCode,
												quote.deliveryAddress.city,
											]
												.filter(Boolean)
												.join(' '),
											quote.deliveryAddress.countryCode,
										].filter((line): line is string => !!line)
									: []
							}
						/>
					</div>
				</>
			),
		},
		{
			id: 'timeline',
			label: 'Timeline',
			tabGroup: DETAILS_TAB,
			content: (
				<>
					<SectionHead title="Timeline" meta="when this quote moved" />
					<QuoteFacts
						title="Dates"
						facts={[
							{
								label: 'Created',
								value: quote.createdAt ? formatDate(quote.createdAt) : '—',
								muted: !quote.createdAt,
							},
							...(quote.offerRequestedAt
								? [
										{
											label: 'Offer requested',
											value: formatDate(quote.offerRequestedAt),
										},
									]
								: []),
							{
								label: 'Last updated',
								value: quote.updatedAt ? formatDate(quote.updatedAt) : '—',
								muted: !quote.updatedAt,
							},
						]}
					/>
				</>
			),
		},
	];

	return (
		<div className="mx-auto max-w-[var(--content-max)] px-4 pb-20 sm:px-6 md:px-8">
			<QuoteHeader
				name={quote.name}
				status={status}
				subtitle={
					<>
						<span className="font-mono" title={quote.id}>
							{quote.id.slice(0, 8).toUpperCase()}
						</span>
						{quote.createdAt && <span> · Created {formatDate(quote.createdAt)}</span>}
					</>
				}
				actions={
					<>
						{isEditable && (
							<Button
								variant="outline"
								size="sm"
								onClick={() => setEditDetailsOpen(true)}
								disabled={readOnly}
								title={readOnlyReason}
							>
								<Pencil size={14} data-icon="inline-start" />
								Edit quote
							</Button>
						)}
						{canRequestOffer && (
							/* The hint sits under the button rather than beside it, so it
							   can't widen the action group or push the header onto a
							   second line. */
							<span className="flex flex-col items-end gap-1">
								<Button
									size="sm"
									onClick={handleRequestOffer}
									disabled={!hasItems || requestingOffer || readOnly}
									title={readOnlyReason}
								>
									<Check size={14} data-icon="inline-start" />
									{requestingOffer ? 'Requesting…' : 'Request offer'}
								</Button>
								{!hasItems && (
									<span className="text-xs text-text-4">
										Add a product to request an offer
									</span>
								)}
							</span>
						)}
						{quote.status === OrderQuoteStatus.Requested && (
							<span className="flex items-center gap-1.5 text-sm text-text-3">
								<Check size={12} className="text-status-success" />
								Requested
								{quote.offerRequestedAt
									? ` ${formatDate(quote.offerRequestedAt)}`
									: ''}{' '}
								— awaiting offer
							</span>
						)}
					</>
				}
			/>

			<QuoteOverview stats={stats} stages={stages} />

			<QuoteSections
				sections={sections}
				/* A post-deadline acceptance waiting on us — above the strip, because
				   nothing else on the page matters until it is resolved, including
				   whichever tab happens to be open. */
				lead={
					quote.status === OrderQuoteStatus.PendingReview && (
						<div className="flex items-start gap-3 rounded-[var(--r-3)] border border-status-info/25 bg-status-info/5 px-4 py-3.5">
							<Clock size={18} className="mt-0.5 shrink-0 text-status-info" />
							<div>
								<MicroLabel className="text-status-info">
									Awaiting confirmation
								</MicroLabel>
								<div className="mt-1 text-sm leading-relaxed text-text-3">
									You accepted after the offer’s acceptance window had passed, so
									no order has been created yet. Our team will review and confirm
									your acceptance shortly — you’ll be notified once it’s ready.
								</div>
							</div>
						</div>
					)
				}
			/>

			{/* ── Modals ── */}
			<EditOrderQuoteDetailsModal
				open={editDetailsOpen}
				onOpenChange={setEditDetailsOpen}
				quoteId={quoteId}
				initialValues={{
					name: quote.name,
					description: quote.description,
					purpose: quote.purpose,
					purposeOther: quote.purposeOther,
					deadline: quote.deadline,
				}}
				onSuccess={refetchQuote}
			/>

			<EditOrderQuoteAddressModal
				open={editAddressOpen}
				onOpenChange={setEditAddressOpen}
				quoteId={quoteId}
				organizationId={quote.organizationId}
				initialValue={quote.deliveryAddress ?? null}
				onSuccess={refetchQuote}
			/>

			<AddProductsToQuoteModal
				open={addItemsOpen}
				onOpenChange={setAddItemsOpen}
				quoteId={quoteId}
				existingProductIds={items.map((i) => i.productId)}
				onSuccess={refetchQuote}
			/>

			<EditOrderQuoteItemModal
				open={!!editItem}
				onOpenChange={(v) => !v && setEditItem(null)}
				item={editItem}
				onSuccess={refetchQuote}
			/>

			<ConfirmDialog
				open={!!removeItemId}
				onOpenChange={(v) => !v && setRemoveItemId(null)}
				title="Remove item"
				description="Are you sure you want to remove this item from the quote?"
				confirmLabel="Remove"
				loading={removingItem}
				onConfirm={handleRemoveItem}
			/>

			{viewer && (
				<FilePreview
					files={viewer.files}
					initialIndex={viewer.index}
					onClose={() => setViewer(null)}
				/>
			)}
		</div>
	);
}

/* ── helpers ── */

/** The requested date and how many days are left. Neutral — it is a record of
 * what was asked for, not a status. */
function deadlineLabel(deadline?: string | null): string | null {
	if (!deadline) return null;
	const days = Math.ceil((new Date(deadline).getTime() - Date.now()) / 86_400_000);
	const countdown =
		days < 0 ? `Expired ${Math.abs(days)}d ago` : days === 0 ? 'Due today' : `${days}d left`;
	return `${countdown} · ${formatDate(deadline)}`;
}

/**
 * The page's own chrome, empty — the title, the subtitle and the stat strip,
 * which are the page's to size. The section area below is the package's own
 * skeleton, drawn to the same measurements as the real layouts so the page loads
 * into the shape it is about to show.
 */
function DetailSkeleton() {
	return (
		<div className="mx-auto max-w-[var(--content-max)] px-4 pt-6 pb-20 sm:px-6 md:px-8">
			<div className="mb-2 h-7 w-56 animate-pulse rounded bg-surface-fill" />
			<div className="mb-6 h-4 w-72 animate-pulse rounded bg-surface-fill" />
			<div className="h-[130px] animate-pulse rounded-[var(--r-3)] bg-surface-fill" />
			<QuoteSectionsSkeleton />
		</div>
	);
}
